import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Sparkles, CheckCircle2, AlertTriangle,
  Moon, Activity, Heart, Flame, User2, Stethoscope,
} from 'lucide-react';

/**
 * Hormonal Health — a deep, gender-aware 4-step assessment.
 *
 * Routes:
 *   /hormonal-health?context=women   → cycle / hormonal balance / menopause
 *   /hormonal-health?context=men     → testosterone / energy / libido / mood
 *
 * Mirrors the visual language and step pattern of HealthRiskCheck so the
 * "Daily Check → Risk Check → Hormonal" journey feels like one product.
 */

const STEPS = ['age', 'symptoms', 'lifestyle', 'patterns', 'result'];

const WOMEN_SYMPTOMS = [
  { id: 'irregular_cycle', label: 'Irregular periods' },
  { id: 'heavy_periods', label: 'Heavy or painful periods' },
  { id: 'mood_swings', label: 'Mood swings or PMS' },
  { id: 'hot_flashes', label: 'Hot flashes / night sweats' },
  { id: 'fatigue', label: 'Persistent fatigue' },
  { id: 'sleep_issues', label: 'Trouble sleeping' },
  { id: 'low_libido', label: 'Low libido' },
  { id: 'weight_gain', label: 'Unexpected weight gain' },
  { id: 'skin_hair', label: 'Skin / hair / acne changes' },
  { id: 'brain_fog', label: 'Brain fog' },
];

const MEN_SYMPTOMS = [
  { id: 'low_energy', label: 'Persistent low energy' },
  { id: 'low_libido', label: 'Low libido' },
  { id: 'erection_changes', label: 'Erection changes' },
  { id: 'mood_low', label: 'Low mood / irritability' },
  { id: 'muscle_loss', label: 'Loss of muscle / strength' },
  { id: 'belly_fat', label: 'Increased belly fat' },
  { id: 'sleep_issues', label: 'Trouble sleeping' },
  { id: 'brain_fog', label: 'Brain fog / poor focus' },
  { id: 'hair_thinning', label: 'Hair thinning' },
  { id: 'sweats', label: 'Night sweats' },
];

const LIFESTYLE_QUESTIONS = [
  { id: 'sleep', label: 'I usually get 7+ hours of sleep', icon: Moon },
  { id: 'movement', label: 'I exercise or walk most days', icon: Activity },
  { id: 'meals', label: 'I eat balanced meals (not mostly processed food)', icon: Heart },
  { id: 'stress', label: "I have a way to manage stress most days", icon: Sparkles },
];

const WOMEN_PATTERNS = [
  { id: 'very_regular', label: 'Very regular' },
  { id: 'mostly_regular', label: 'Mostly regular' },
  { id: 'irregular', label: 'Irregular' },
  { id: 'no_periods', label: 'No periods (menopausal / on contraception)' },
];

const MEN_PATTERNS = [
  { id: 'morning_strong', label: 'Strong morning energy & libido' },
  { id: 'morning_okay', label: 'Okay morning energy' },
  { id: 'morning_low', label: 'Low morning energy / libido' },
  { id: 'crashes', label: 'Energy crashes through the day' },
];

const lifeStageWomen = (age) => {
  if (age < 25) return 'Early adulthood';
  if (age < 40) return 'Reproductive years';
  if (age < 52) return 'Perimenopause window';
  return 'Menopause / post-menopause';
};
const lifeStageMen = (age) => {
  if (age < 30) return 'Peak hormonal years';
  if (age < 45) return 'Stable adult years';
  if (age < 60) return 'Andropause window';
  return 'Senior years';
};

const computeProfile = ({ context, age, symptoms, lifestyle, pattern }) => {
  const ageNum = parseInt(age, 10) || 0;
  let imbalanceScore = symptoms.length * 2;

  // Lifestyle deductions — good lifestyle reduces score
  const goodLifestyle = LIFESTYLE_QUESTIONS.filter((q) => lifestyle.includes(q.id)).length;
  imbalanceScore -= goodLifestyle; // up to -4

  // Pattern weight
  if (context === 'women') {
    if (pattern === 'irregular') imbalanceScore += 3;
    if (pattern === 'no_periods' && ageNum < 45) imbalanceScore += 2;
  } else {
    if (pattern === 'morning_low') imbalanceScore += 3;
    if (pattern === 'crashes') imbalanceScore += 2;
  }

  // Age weighting
  if (context === 'women' && ageNum >= 40) imbalanceScore += 1;
  if (context === 'men' && ageNum >= 45) imbalanceScore += 1;

  imbalanceScore = Math.max(0, imbalanceScore);

  let band = 'balanced';
  if (imbalanceScore >= 10) band = 'notable';
  else if (imbalanceScore >= 5) band = 'mild';

  // Identify the top signal area
  const topSignal = (() => {
    if (context === 'women') {
      if (symptoms.includes('hot_flashes') || symptoms.includes('irregular_cycle')) return 'Estrogen rhythm';
      if (symptoms.includes('mood_swings') || symptoms.includes('brain_fog')) return 'Mood & cognition';
      if (symptoms.includes('low_libido') || symptoms.includes('skin_hair')) return 'Androgen balance';
      if (symptoms.includes('fatigue') || symptoms.includes('sleep_issues')) return 'Cortisol & recovery';
      return 'General balance';
    }
    if (symptoms.includes('low_libido') || symptoms.includes('erection_changes') || symptoms.includes('muscle_loss')) return 'Testosterone';
    if (symptoms.includes('low_energy') || symptoms.includes('mood_low')) return 'Energy & mood';
    if (symptoms.includes('sleep_issues') || symptoms.includes('sweats')) return 'Cortisol & recovery';
    if (symptoms.includes('belly_fat')) return 'Insulin & metabolism';
    return 'General balance';
  })();

  // Recommendations
  const recs = [];
  if (!lifestyle.includes('sleep')) recs.push('Protect 7–8 hours of sleep — it is the single strongest hormonal regulator.');
  if (!lifestyle.includes('movement')) recs.push('Add 20–30 minutes of movement most days — strength work especially supports hormones.');
  if (!lifestyle.includes('meals')) recs.push('Build meals around protein, fibre, and healthy fats — stabilises insulin and mood.');
  if (!lifestyle.includes('stress')) recs.push('Build a 5-minute calm ritual into your day — chronic cortisol disrupts every other hormone.');
  if (context === 'women') {
    if (symptoms.includes('irregular_cycle')) recs.push('Track your cycle for 3 months — a clinician can spot patterns from the data.');
    if (symptoms.includes('hot_flashes')) recs.push('Hot flashes respond to layered clothing, cool sleep, and limiting alcohol & spicy food.');
    if (symptoms.includes('mood_swings')) recs.push('Omega-3 rich foods (oily fish, flaxseed) support mood across the cycle.');
  } else {
    if (symptoms.includes('low_libido') || symptoms.includes('erection_changes')) recs.push('Cardio + strength training 3–4×/week is the most evidence-backed boost to testosterone.');
    if (symptoms.includes('belly_fat')) recs.push('Reducing refined carbs and added sugar lowers insulin — which lifts free testosterone.');
    if (symptoms.includes('sleep_issues')) recs.push('Most testosterone is produced during deep sleep — protect a consistent bedtime.');
  }
  if (recs.length === 0) recs.push("You're already supporting your hormonal balance well. Keep your daily check-ins going.");

  const lifeStage = context === 'women' ? lifeStageWomen(ageNum) : lifeStageMen(ageNum);

  const consultFlag = symptoms.length >= 4 || band === 'notable';

  return { band, topSignal, recs, lifeStage, consultFlag, imbalanceScore };
};

const BAND_STYLE = {
  balanced: { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Balanced' },
  mild: { text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Mild Imbalance' },
  notable: { text: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', label: 'Notable Imbalance' },
};

const HormonalHealth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const context = searchParams.get('context') === 'men' ? 'men' : 'women';

  const [stepIdx, setStepIdx] = useState(0);
  const [age, setAge] = useState('');
  const [symptoms, setSymptoms] = useState([]);
  const [lifestyle, setLifestyle] = useState([]);
  const [pattern, setPattern] = useState('');

  const step = STEPS[stepIdx];
  const progress = useMemo(() => ((stepIdx + 1) / STEPS.length) * 100, [stepIdx]);

  const palette = context === 'women'
    ? { from: 'from-fuchsia-500', to: 'to-pink-500', accent: 'text-fuchsia-700', soft: 'bg-fuchsia-50', border: 'border-fuchsia-200', ring: 'focus:border-fuchsia-400', activeBorder: 'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-900', hoverBorder: 'hover:border-fuchsia-300', tagText: 'text-fuchsia-700', dot: 'bg-fuchsia-500' }
    : { from: 'from-amber-500', to: 'to-orange-500', accent: 'text-amber-700', soft: 'bg-amber-50', border: 'border-amber-200', ring: 'focus:border-amber-400', activeBorder: 'border-amber-500 bg-amber-50 text-amber-900', hoverBorder: 'hover:border-amber-300', tagText: 'text-amber-700', dot: 'bg-amber-500' };

  const symptomsList = context === 'women' ? WOMEN_SYMPTOMS : MEN_SYMPTOMS;
  const patternsList = context === 'women' ? WOMEN_PATTERNS : MEN_PATTERNS;
  const patternsTitle = context === 'women' ? 'Cycle pattern' : 'Daily energy & libido pattern';
  const patternsHelper = context === 'women'
    ? 'How regular is your cycle right now?'
    : 'Which line describes your typical day?';

  const toggle = (list, setList, id) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const canAdvance = () => {
    if (step === 'age') return /^\d{1,3}$/.test(age) && parseInt(age, 10) >= 12 && parseInt(age, 10) <= 110;
    if (step === 'patterns') return pattern !== '';
    return true;
  };

  const next = () => canAdvance() && setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const result = useMemo(() => {
    if (step !== 'result') return null;
    return computeProfile({ context, age, symptoms, lifestyle, pattern });
  }, [step, context, age, symptoms, lifestyle, pattern]);

  const homeRoute = context === 'women' ? '/womens-health' : '/mens-health';
  const homeLabel = context === 'women' ? "Women's Health" : "Men's Health";

  return (
    <div className={`min-h-screen bg-gradient-to-b ${context === 'women' ? 'from-pink-50/40' : 'from-amber-50/40'} via-white to-white pb-24`} data-testid="hormonal-health-flow">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => (stepIdx === 0 ? navigate(homeRoute) : back())}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4"
          data-testid="hormonal-back"
        >
          <ArrowLeft className="h-4 w-4" />
          {stepIdx === 0 ? `Back to ${homeLabel}` : 'Back'}
        </button>

        <header className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${palette.from} ${palette.to} flex items-center justify-center shrink-0`}>
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight" data-testid="hormonal-title">
                Hormonal Health
              </h1>
              <p className="text-xs text-gray-500 mt-0.5 inline-flex items-center gap-1.5">
                <User2 className="h-3 w-3" />
                {context === 'women' ? "Women's flow" : "Men's flow"} · 60-second snapshot. Not a diagnosis.
              </p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-4">
            <div
              className={`h-full bg-gradient-to-r ${palette.from} ${palette.to} transition-all duration-300`}
              style={{ width: `${progress}%` }}
              data-testid="hormonal-progress"
            />
          </div>
        </header>

        {step === 'age' && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="hormonal-step-age">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 1 of 4</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">How old are you?</h2>
            <p className="text-sm text-gray-600 mb-4">Hormonal patterns shift with life stage.</p>
            <input
              type="number"
              inputMode="numeric"
              min="12"
              max="110"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="Enter your age"
              className={`w-full text-lg font-semibold border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none ${palette.ring}`}
              data-testid="hormonal-age-input"
            />
          </section>
        )}

        {step === 'symptoms' && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="hormonal-step-symptoms">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 2 of 4</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Symptoms you've noticed</h2>
            <p className="text-sm text-gray-600 mb-4">Select what applies. Skip if none.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {symptomsList.map((opt) => {
                const active = symptoms.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggle(symptoms, setSymptoms, opt.id)}
                    className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                      active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder} text-gray-700`
                    }`}
                    data-testid={`hormonal-symptom-${opt.id}`}
                  >
                    {active && <CheckCircle2 className={`h-4 w-4 inline mr-1.5 ${palette.accent}`} />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 'lifestyle' && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="hormonal-step-lifestyle">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 3 of 4</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Lifestyle foundations</h2>
            <p className="text-sm text-gray-600 mb-4">Tick each statement that's true for you most of the time.</p>
            <div className="space-y-2">
              {LIFESTYLE_QUESTIONS.map((q) => {
                const Qi = q.icon;
                const active = lifestyle.includes(q.id);
                return (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => toggle(lifestyle, setLifestyle, q.id)}
                    className={`w-full text-left flex items-center gap-3 rounded-xl border-2 px-3 py-3 transition-all ${
                      active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder}`
                    }`}
                    data-testid={`hormonal-lifestyle-${q.id}`}
                  >
                    <span className="w-9 h-9 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                      <Qi className="h-4 w-4 text-gray-700" />
                    </span>
                    <span className="text-sm font-medium text-gray-800 flex-1">{q.label}</span>
                    {active && <CheckCircle2 className={`h-4 w-4 ${palette.accent}`} />}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 'patterns' && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5" data-testid="hormonal-step-patterns">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">Step 4 of 4</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">{patternsTitle}</h2>
            <p className="text-sm text-gray-600 mb-4">{patternsHelper}</p>
            <div className="grid grid-cols-1 gap-2">
              {patternsList.map((opt) => {
                const active = pattern === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPattern(opt.id)}
                    className={`text-left text-sm font-medium rounded-xl border-2 px-3 py-3 transition-all ${
                      active ? palette.activeBorder : `border-gray-200 ${palette.hoverBorder} text-gray-700`
                    }`}
                    data-testid={`hormonal-pattern-${opt.id}`}
                  >
                    {active && <CheckCircle2 className={`h-4 w-4 inline mr-1.5 ${palette.accent}`} />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 'result' && result && (
          <section data-testid="hormonal-result">
            {result.consultFlag && (
              <div className="rounded-2xl border-2 border-rose-200 bg-rose-50 p-4 mb-4 flex gap-3" data-testid="hormonal-consult-flag">
                <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-rose-900">Worth a clinician's eye</p>
                  <p className="text-sm text-rose-800 mt-1">
                    You flagged several signals. A short conversation with your GP or an
                    endocrinologist can rule out underlying causes. This tool is not a diagnosis.
                  </p>
                </div>
              </div>
            )}

            <div className={`rounded-2xl border-2 ${BAND_STYLE[result.band].border} ${BAND_STYLE[result.band].bg} p-5 mb-4`}>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Hormonal snapshot</p>
              <h2 className="text-2xl font-bold text-gray-900">
                <span className={BAND_STYLE[result.band].text}>{BAND_STYLE[result.band].label}</span>
              </h2>
              <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                Your strongest signal today is around <span className="font-semibold">{result.topSignal}</span>.
                Life stage: <span className="font-semibold">{result.lifeStage}</span>.
              </p>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">What we noticed</p>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2.5">
                  <Flame className={`h-4 w-4 ${palette.accent} shrink-0 mt-0.5`} />
                  <p className="text-sm text-gray-700">
                    {result.imbalanceScore === 0
                      ? 'No notable imbalance signals on the questions you answered today.'
                      : `${symptoms.length} symptom${symptoms.length === 1 ? '' : 's'} flagged · ${LIFESTYLE_QUESTIONS.length - lifestyle.length} of 4 lifestyle foundations to strengthen.`}
                  </p>
                </li>
                <li className="flex items-start gap-2.5">
                  <Stethoscope className={`h-4 w-4 ${palette.accent} shrink-0 mt-0.5`} />
                  <p className="text-sm text-gray-700">
                    {context === 'women'
                      ? 'Cycle, sleep, stress and nutrition shape estrogen, progesterone, and cortisol together — small habits move all four.'
                      : 'Sleep, strength training, body fat, and stress together regulate testosterone and energy more than any single fix.'}
                  </p>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4">
              <p className={`text-[11px] font-semibold ${palette.tagText} uppercase tracking-widest mb-3`}>Where to start</p>
              <ul className="space-y-2.5">
                {result.recs.map((r) => (
                  <li key={r} className="flex items-start gap-2.5">
                    <CheckCircle2 className={`h-4 w-4 ${palette.accent} shrink-0 mt-0.5`} />
                    <p className="text-sm text-gray-700 leading-relaxed">{r}</p>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-[11px] text-gray-400 text-center mb-4">
              HealthLoop supports awareness — it does not diagnose or replace medical care.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/preventive-health')}
                className={`bg-gradient-to-br ${palette.from} ${palette.to} text-white font-semibold rounded-xl px-5 py-3 shadow-sm transition-all`}
                data-testid="hormonal-cta-track"
              >
                Track my metrics →
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-xl px-5 py-3 border border-gray-200 transition-all"
                data-testid="hormonal-cta-dashboard"
              >
                Back to dashboard
              </button>
            </div>
          </section>
        )}

        {step !== 'result' && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={next}
              disabled={!canAdvance()}
              className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold shadow-sm transition-all ${
                canAdvance()
                  ? `bg-gradient-to-br ${palette.from} ${palette.to} hover:brightness-110 text-white`
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="hormonal-next"
            >
              {stepIdx === STEPS.length - 2 ? 'Get my snapshot' : 'Continue'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HormonalHealth;
